import {Component, DestroyRef, HostListener, inject, Input, OnInit, ViewEncapsulation} from '@angular/core';
import { DatePipe } from '@angular/common';
import {BehaviorSubject, combineLatestWith, Observable} from 'rxjs';
import { filter, map, startWith, take, tap, withLatestFrom } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { FormBuilder } from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {LocationSetTypesFacadeService} from '@emm/management/services/location-set-types-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-location-set-types-form',
    templateUrl: './location-set-types-form.component.html',
    styleUrls: ['./location-set-types-form.component.scss'],
})
export class LocationSetTypesFormComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = new BehaviorSubject<boolean>(null);
    @Input() isSuperAdmin: boolean;
    disabled$ = this.locationSetTypesFacade.anyLoading$.pipe(combineLatestWith(this.isReadOnly),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    form;
    locationSetTypeDetail$ = this.locationSetTypesFacade.locationSetTypeDetail$;
    model = {};
    pipe = new DatePipe('en-US');

    attributes$ = this.locationSetTypeDetail$.pipe(
        filter(RA.isNotNil),
        tap(),
        map((item: any) => item.attributes),
        startWith([] as ItemAttribute[])
    );

    model$ = this.locationSetTypeDetail$.pipe(
        filter(RA.isNotNil),
        map((locationSetType: LocationSetType) => (this.form = this.formBuilder.group({
            locked: [locationSetType.locked, {disabled: this.isReadOnly.value, nonNullable: true}],
            name: [locationSetType.name, {disabled: this.isReadOnly.value, nonNullable: true}],
            weight: [locationSetType.weight, {disabled: this.isReadOnly.value, nonNullable: true}],
            description: [locationSetType.description, {disabled: this.isReadOnly.value}],
        })))
    );

    constructor(
        private authFacade: AuthFacadeService,
        private locationSetTypesFacade: LocationSetTypesFacadeService,
        private notificationService: NotificationService,
        private formBuilder: FormBuilder
    ) {}



    ngOnInit(): void {
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.isSuperAdmin) {
                this.form.disable();
            }
        });
        this.disabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.isReadOnly$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isReadOnly: boolean) => {
            this.isReadOnly.next(isReadOnly);
        });
    }



    cancel(): void {
        this.locationSetTypeDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (locationSetTypeDetail: LocationSetType) => this.model = locationSetTypeDetail);
        this.form.reset(this.model);
    }



    submit(locationSetTypeTag: string, reloadAfterUpdate: boolean = true): void {
        const formData = {
            description: this.form.controls.description.value,
            locked: this.form.controls.locked.value,
            name: this.form.controls.name.value,
            tag: locationSetTypeTag,
            weight: this.form.controls.weight.value,
        };
        this.locationSetTypesFacade
            .updateLocationSetType(locationSetTypeTag, formData, reloadAfterUpdate)
            .pipe(withLatestFrom(this.locationSetTypeDetail$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([success, locationSetType]) => {
                if (success) {
                    if (this.form.updateInitialValue) {
                        this.form.updateInitialValue();
                    }
                    this.notificationService.success('Saved : ' + locationSetType.name);
                    this.form.reset(formData);
                    return;
                }
                this.notificationService.error('Could not save : ' + locationSetType.name);
            });
    }

    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }

    isFormReady(): boolean {
        return this.form && this.form.dirty && !this.form.invalid;
    }



}
